"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChevronDown, ChevronRight, Menu, Sparkles } from "lucide-react";

import { getNavLabel, getNavLinkLabel } from "@/content/i18n/navigation";
import { t } from "@/content/i18n/messages";
import { servicesList } from "@/lib/data";
import {
  localeConfigs,
  locales,
  getLocaleFromPathname,
  getLocalizedPathname,
  isEnglishOnlyRoute,
  stripLocalePrefix,
  type Locale,
} from "@/lib/i18n";
import { getServiceRoute } from "@/lib/serviceUtils";
import { cn } from "@/lib/utils";
import {
  ENTERPRISE_ACTIVE_PATHS,
  ENTERPRISE_MENU_GROUPS,
  ENTERPRISE_MENU_LINKS,
} from "@/lib/nav-enterprise";

type DropdownType = "company" | "services" | "ai-agentic" | "enterprise";

interface NavLinkConfig {
  href: string;
  label: string;
  hasDropdown?: boolean;
  dropdownType?: DropdownType;
}

interface SubLinkConfig {
  href: string;
  label: string;
}

const logoPath = "/images/logo.png";

// Top nav focuses on the AI/agentic proposition. The non-AI "Services" full-stack
// dropdown was removed from the menu (its pages stay routed + crawlable via CrawlableNav).
const NAV_LINKS: NavLinkConfig[] = [
  { href: "/", label: "Home" },
  { href: "/services/ai-solutions/", label: "AI & Agentic", hasDropdown: true, dropdownType: "ai-agentic" },
  { href: "/enterprise/", label: "Enterprise", hasDropdown: true, dropdownType: "enterprise" },
  { href: "/company/", label: "Company", hasDropdown: true, dropdownType: "company" },
  { href: "/singapore/", label: "Singapore" },
  { href: "/blog/", label: "Blog" },
  { href: "/contact/", label: "Contact" },
];

// Full company list — kept for the crawlable (sr-only) nav so every page stays linked.
const COMPANY_LINKS: SubLinkConfig[] = [
  { href: "/about-us/", label: "About Us" },
  { href: "/our-people/", label: "Our People" },
  { href: "/corporate-values/", label: "Corporate Values" },
  { href: "/corporate-mission/", label: "Corporate Mission" },
  { href: "/industries-we-serve/", label: "Industries We Serve" },
  { href: "/our-partners/", label: "Our Partners" },
  { href: "/partners/anthropic-claude-partner/", label: "Claude Partner" },
  { href: "/partners/openai-partner/", label: "OpenAI Partner" },
  { href: "/anthropic-claude-partner-malaysia-singapore/", label: "Why we choose Claude" },
  { href: "/careers/", label: "Careers" },
  { href: "/blog/intern-story-product-growth/", label: "Life at Anchor Sprint" },
];

// Trimmed set actually shown in the Company dropdown (values/mission/industries-we-serve
// dropped from the menu — pages stay live + crawlable).
const COMPANY_DROPDOWN: SubLinkConfig[] = [
  { href: "/about-us/", label: "About Us" },
  { href: "/our-people/", label: "Our People" },
  { href: "/partners/anthropic-claude-partner/", label: "Claude Partner" },
  { href: "/partners/openai-partner/", label: "OpenAI Partner" },
  { href: "/anthropic-claude-partner-malaysia-singapore/", label: "Why we choose Claude" },
  { href: "/our-partners/", label: "Our Partners" },
  { href: "/careers/", label: "Careers" },
  { href: "/blog/intern-story-product-growth/", label: "Life at Anchor Sprint" },
];

interface MenuGroup {
  label: string;
  items: SubLinkConfig[];
}

// The single "AI & Agentic" mega-menu — grouped into scannable columns. Labels are
// short; the group header gives the context. (The Ezy product pages were retired
// 2026-10; EzyChat now lives at ezychat.ai and the rest are in private testing, so
// there is no Products group.)
const AI_MENU_GROUPS: MenuGroup[] = [
  {
    label: "Solutions",
    items: [
      { href: "/services/ai-agents/", label: "AI Agent Development" },
      {
        href: "/services/claude-managed-service-malaysia/",
        label: "Claude Managed Service",
      },
      { href: "/services/training-education/", label: "Claude & AI Training" },
      { href: "/academy/", label: "Anchor Academy" },
      { href: "/solutions/agent-rescue/", label: "Agent Rescue" },
      { href: "/services/conversational-ai/", label: "Conversational AI" },
      { href: "/services/seo-services-malaysia/", label: "AI SEO Agency" },
      { href: "/services/ai-solutions/", label: "AI Solutions overview" },
    ],
  },
  {
    label: "Use cases",
    items: [
      { href: "/solutions/agent-use-cases/", label: "Use Case Directory" },
      { href: "/solutions/ai-agents-for-logistics/", label: "Logistics" },
      { href: "/solutions/ai-agents-for-retail/", label: "Retail & F&B" },
    ],
  },
  {
    label: "Resources",
    items: [
      { href: "/ai-governance/", label: "AI Governance" },
      { href: "/guides/malaysia-budget-2027/", label: "Budget 2027 Guide" },
      { href: "/anthropic-claude-partner-malaysia-singapore/", label: "Why we choose Claude" },
      { href: "/singapore/claude-partner/", label: "Claude Partner Singapore" },
      { href: "/guides/how-to-use-claude-malaysia/", label: "Using Claude" },
      { href: "/guides/claude-plans-malaysia/", label: "Plans & Pricing" },
      { href: "/guides/chatgpt-plans-malaysia/", label: "ChatGPT plans" },
      { href: "/guides/chatgpt-intelligent-ui/", label: "ChatGPT Intelligent UI" },
      { href: "/tools/claude-cost-calculator/", label: "Cost Calculator" },
      { href: "/guides/claude-privacy-compliance-malaysia/", label: "Privacy & Compliance" },
      { href: "/guides/claude-cowork/", label: "Claude Cowork" },
    ],
  },
];

// Flattened for the mobile menu, crawlable nav, and the DROPDOWN_LINKS fallback.
const AI_SERVICE_LINKS: SubLinkConfig[] = AI_MENU_GROUPS.flatMap(
  (group) => group.items,
);

// Remaining services keep their existing order (AI Solutions is covered above).
const OTHER_SERVICE_LINKS: SubLinkConfig[] = servicesList
  .filter((service) => !["as", "te"].includes(service.detailId))
  .map((service) => ({
    href: getServiceRoute(service.detailId),
    label: service.title,
  }));

// Dropdowns rendered as grouped mega-menus (columns with group headers).
const MEGA_MENU_GROUPS: Partial<Record<DropdownType, MenuGroup[]>> = {
  "ai-agentic": AI_MENU_GROUPS,
  enterprise: ENTERPRISE_MENU_GROUPS,
};

const DROPDOWN_LINKS: Record<DropdownType, SubLinkConfig[]> = {
  "ai-agentic": AI_SERVICE_LINKS,
  enterprise: ENTERPRISE_MENU_LINKS,
  company: COMPANY_DROPDOWN,
  // AI services now live under the dedicated "AI & Agentic" menu, so the
  // Services dropdown lists only the non-AI offerings (no duplication).
  services: OTHER_SERVICE_LINKS,
};

const DROPDOWN_METADATA: Record<DropdownType, { heading: string; description: string }> = {
  "ai-agentic": {
    heading: "AI & Agentic",
    description: "AI agent development, Claude onboarding and managed operations, and practical training for Malaysian businesses.",
  },
  enterprise: {
    heading: "Enterprise",
    description: "Enterprise software and hardware, compliance guides, and resilience services for regulated teams.",
  },
  company: {
    heading: "Company Overview",
    description: "Get to know the team, mission, and values guiding Anchor Sprint.",
  },
  services: {
    heading: "Our Services",
    description: "Explore the full spectrum of product, design, and growth offerings.",
  },
};

const LANGUAGE_LABELS: Partial<Record<Locale, string>> = {
  en: "EN",
  ms: "BM",
  zh: "\u4e2d\u6587",
};

/*
  Crawlable internal-link path (same pattern as app/blog/page.tsx). The desktop
  dropdowns and the mobile Radix Sheet only mount their links on open, so the
  static HTML of every page ships without header links to guides, services,
  or company pages. This always-rendered, visually hidden (sr-only)
  nav puts a plain <a href> for every header destination into the prerendered
  HTML so crawlers and the no-JS first pass can follow them. It lives outside
  any Radix component so it server-renders unconditionally.
*/
// Localized (2026-10-08): this list used to render English labels and English
// hrefs on every /ms/ and /zh/ page, so each Malay/Chinese page carried ~60
// English anchors ("Claude Partner", "Claude Partner Singapore", ...). That
// English partner wording is one reason /ms/ pages were being credited with
// English queries such as "claude partner malaysia".
const CrawlableNav = ({ locale }: { locale: Locale }) => {
  const crawlableLinks: SubLinkConfig[] = [
    ...NAV_LINKS.map(({ href, label }) => ({ href, label })),
    ...AI_SERVICE_LINKS,
    ...ENTERPRISE_MENU_LINKS,
    ...OTHER_SERVICE_LINKS,
    ...COMPANY_LINKS,
  ];

  // Some top-level NAV_LINKS entries point at the same page as a dropdown item
  // (e.g. /services/ai-solutions/); dedupe by href so each destination appears once.
  const uniqueLinks = Array.from(
    new Map(crawlableLinks.map((item) => [item.href, item])).values(),
  );

  return (
    <nav aria-label="All pages" className="sr-only">
      <ul>
        {uniqueLinks.map((item) => (
          <li key={item.href}>
            <Link href={navHref(item.href, locale)} prefetch={false}>
              {getNavLinkLabel(locale, item.href, getNavLabel(locale, item.label))}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const getLanguageLabel = (locale: Locale) =>
  LANGUAGE_LABELS[locale] ?? String(locale).toUpperCase();

const getLanguageName = (locale: Locale) => localeConfigs[locale].label;

const getLabel = (locale: Locale, label: string) =>
  getNavLabel(locale, label);

const getLinkLabel = (locale: Locale, item: SubLinkConfig) =>
  getNavLinkLabel(locale, item.href, item.label);

const getComparablePathname = (pathname: string) => {
  const strippedPathname = stripLocalePrefix(pathname);

  return strippedPathname === "/"
    ? "/"
    : strippedPathname.replace(/\/+$/, "");
};

const COMPANY_ACTIVE_PATHS = [
  "/our-people",
  "/our-people/",
  "/company",
  "/company/",
  "/about-us",
  "/about-us/",
  "/corporate-values",
  "/corporate-values/",
  "/corporate-mission",
  "/corporate-mission/",
  "/industries-we-serve",
  "/industries-we-serve/",
  "/our-partners",
  "/our-partners/",
  "/partners/openai-partner",
  "/partners/openai-partner/",
];

const isCompanyRoute = (pathname: string) => {
  const currentPathname = getComparablePathname(pathname);

  return COMPANY_ACTIVE_PATHS.some(
    (path) => currentPathname === getComparablePathname(path),
  );
};

const AI_AGENTIC_ACTIVE_PATHS = [
  "/services/ai-solutions",
  "/services/claude-managed-service-malaysia",
  "/services/ai-agents",
  "/services/conversational-ai",
];

const isAiAgenticRoute = (pathname: string) => {
  const currentPathname = getComparablePathname(pathname);

  return AI_AGENTIC_ACTIVE_PATHS.some(
    (path) => currentPathname === path || currentPathname.startsWith(`${path}/`),
  );
};

const isEnterpriseRoute = (pathname: string) => {
  const currentPathname = getComparablePathname(pathname);

  return ENTERPRISE_ACTIVE_PATHS.some(
    (path) => currentPathname === path || currentPathname.startsWith(`${path}/`),
  );
};

const isServicesRoute = (pathname: string) => {
  const currentPathname = getComparablePathname(pathname);

  // AI segment pages belong to the dedicated "AI & Agentic" menu, not Services.
  if (isAiAgenticRoute(pathname)) {
    return false;
  }

  return currentPathname === "/service" || currentPathname.startsWith("/services/");
};

const isNavLinkActive = (link: NavLinkConfig, pathname: string) => {
  if (link.dropdownType === "ai-agentic") {
    return isAiAgenticRoute(pathname);
  }
  if (link.dropdownType === "enterprise") {
    return isEnterpriseRoute(pathname);
  }
  if (link.dropdownType === "company") {
    return isCompanyRoute(pathname);
  }
  if (link.dropdownType === "services") {
    return isServicesRoute(pathname);
  }

  const currentPathname = getComparablePathname(pathname);
  const linkPathname = getComparablePathname(link.href);

  if (linkPathname === "/blog") {
    return currentPathname === "/blog" || currentPathname.startsWith("/blog/");
  }

  return currentPathname === linkPathname;
};

const NavbarLogo = ({ locale }: { locale: Locale }) => {
  const brandName = t(locale, "Anchor Sprint");
  const tagline = t(locale, "AI Digital Agency");

  return (
    <Link href={getLocalizedPathname("/", locale)} className="flex items-center gap-2 sm:gap-3">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-card shadow-sm">
        <img src={logoPath} alt={`${brandName} Logo`} className="h-9 w-9 rounded-lg object-contain" />
      </span>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="text-base font-semibold tracking-tight text-neutral-emphasis">{brandName}</span>
        <span className="whitespace-nowrap text-[0.6rem] font-medium uppercase tracking-[0.12em] text-neutral-muted sm:text-xs sm:tracking-[0.18em]">
          {tagline}
        </span>
      </div>
    </Link>
  );
};

interface DesktopNavigationProps {
  navItems: NavLinkConfig[];
  pathname: string;
  openDropdown: DropdownType | null;
  onToggleDropdown: (type: DropdownType) => void;
  onCloseDropdown: () => void;
  dropdownRefs: Record<DropdownType, React.RefObject<HTMLDivElement>>;
}

const DesktopNavigation = ({
  navItems,
  pathname,
  openDropdown,
  onToggleDropdown,
  onCloseDropdown,
  dropdownRefs,
}: DesktopNavigationProps) => {
  const currentLocale = getLocaleFromPathname(pathname);

  // Desktop bar from xl (1280px): with the Enterprise item added, the bar no longer fits
  // between 1024 and 1279px (it already overflowed at 1024 before), so that range
  // uses the mobile sheet. nowrap keeps longer BM labels ("AI & Agentik") on one line.
  return (
    <nav className="hidden items-center gap-10 xl:flex">
      {navItems.map((link) =>
        link.hasDropdown && link.dropdownType ? (
          <DesktopDropdown
            key={link.href}
            link={link}
            pathname={pathname}
            isOpen={openDropdown === link.dropdownType}
            onToggle={() => onToggleDropdown(link.dropdownType!)}
            onClose={onCloseDropdown}
            dropdownRef={dropdownRefs[link.dropdownType!]}
          />
        ) : (
          <DesktopNavLink key={link.href} link={link} pathname={pathname} />
        ),
      )}
      <LanguageSwitcher pathname={pathname} variant="desktop" />
      <Link href={getLocalizedPathname("/contact/", currentLocale)}>
        <Button className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis">
          {getLabel(currentLocale, "Meet with Us")}
        </Button>
      </Link>
    </nav>
  );
};

interface DesktopNavLinkProps {
  link: NavLinkConfig;
  pathname: string;
}

const DesktopNavLink = ({ link, pathname }: DesktopNavLinkProps) => {
  const currentLocale = getLocaleFromPathname(pathname);
  const isActive = isNavLinkActive(link, pathname);

  return (
    <Link
      href={navHref(link.href, currentLocale)}
      className={cn(
        "relative whitespace-nowrap text-sm font-medium transition-colors after:absolute after:-bottom-1.5 after:left-0 after:right-0 after:h-0.5 after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:content-['']",
        isActive
          ? "text-primary after:scale-x-100"
          : "text-neutral-muted hover:text-primary hover:after:scale-x-100",
      )}
    >
      {getLabel(currentLocale, link.label)}
    </Link>
  );
};

interface DesktopDropdownProps {
  link: NavLinkConfig;
  pathname: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
}

const DesktopDropdown = ({ link, pathname, isOpen, onToggle, onClose, dropdownRef }: DesktopDropdownProps) => {
  const currentLocale = getLocaleFromPathname(pathname);
  const dropdownType = link.dropdownType!;
  const dropdownLinks = DROPDOWN_LINKS[dropdownType];
  const { heading, description } = DROPDOWN_METADATA[dropdownType];
  const isActive = isNavLinkActive(link, pathname);
  const megaGroups = MEGA_MENU_GROUPS[dropdownType];

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={onToggle}
        className={cn(
          "relative flex items-center gap-1 whitespace-nowrap text-sm font-medium transition-colors after:absolute after:-bottom-1.5 after:left-0 after:right-0 after:h-0.5 after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:content-['']",
          isActive
            ? "text-primary after:scale-x-100"
            : "text-neutral-muted hover:text-primary hover:after:scale-x-100",
        )}
      >
        {getLabel(currentLocale, link.label)}
        <ChevronDown className="h-4 w-4 transition-transform" data-state={isOpen ? "open" : "closed"} />
      </button>

      {isOpen ? (
        megaGroups ? (
          <div
            className={cn(
              "absolute top-9 z-30 w-[46rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-card p-6 shadow-lg",
              // "Enterprise" sits mid-bar, so a left-anchored 46rem panel would
              // overflow the viewport at 1280px; centre it under the trigger.
              dropdownType === "enterprise" ? "left-1/2 -translate-x-1/2" : "left-0",
            )}
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-6 lg:grid-cols-3">
              {megaGroups.map((group) => (
                <div key={group.label}>
                  <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-muted">
                    {getLabel(currentLocale, group.label)}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <DropdownLink
                        key={item.href}
                        item={item}
                        pathname={pathname}
                        onNavigate={onClose}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="absolute left-1/2 top-9 z-30 w-[20rem] -translate-x-1/2 rounded-2xl border border-border bg-card p-4 shadow-sm">
            <Link
              href={navHref(link.href, currentLocale)}
              onClick={onClose}
              className="block rounded-2xl border border-border bg-background p-4 transition-colors hover:border-neutral-border"
            >
              <p className="text-sm font-semibold text-neutral-emphasis">
                {getLabel(currentLocale, heading)}
              </p>
              <p className="mt-1 text-xs text-neutral-muted">
                {getLabel(currentLocale, description)}
              </p>
            </Link>
            <div className="mt-4 space-y-1">
              {dropdownLinks.map((item) => (
                <DropdownLink
                  key={item.href}
                  item={item}
                  pathname={pathname}
                  onNavigate={onClose}
                />
              ))}
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};

interface DropdownLinkProps {
  item: SubLinkConfig;
  pathname: string;
  onNavigate: () => void;
}

const DropdownLink = ({ item, pathname, onNavigate }: DropdownLinkProps) => {
  const currentLocale = getLocaleFromPathname(pathname);
  const isActive =
    getComparablePathname(pathname) === getComparablePathname(item.href);

  return (
    <Link
      href={navHref(item.href, currentLocale)}
      onClick={onNavigate}
      className={cn(
        "flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors",
        isActive
          ? "bg-primary/10 font-medium text-primary"
          : "text-neutral-muted hover:bg-neutral-surface hover:text-neutral-emphasis",
      )}
    >
      <span>{getLinkLabel(currentLocale, item)}</span>
      <ChevronRight className="h-4 w-4" />
    </Link>
  );
};

interface MobileNavigationProps {
  navItems: NavLinkConfig[];
  pathname: string;
  onNavigate: () => void;
}

const MobileNavigation = ({ navItems, pathname, onNavigate }: MobileNavigationProps) => {
  const [openSection, setOpenSection] = useState<DropdownType | null>(null);
  const currentLocale = getLocaleFromPathname(pathname);

  const toggleSection = (type: DropdownType) => {
    setOpenSection((previous) => (previous === type ? null : type));
  };

  return (
    <div className="flex flex-col gap-5">
      <MobileNavHeader locale={currentLocale} />

      {navItems.map((link) => {
        if (!link.hasDropdown || !link.dropdownType) {
          return (
            <MobileNavLink
              key={link.href}
              link={link}
              pathname={pathname}
              onNavigate={onNavigate}
            />
          );
        }

        const dropdownType = link.dropdownType;
        const dropdownLinks = DROPDOWN_LINKS[dropdownType];
        const { heading, description } = DROPDOWN_METADATA[dropdownType];
        const megaGroups = MEGA_MENU_GROUPS[dropdownType];

        return (
          <MobileDropdownSection
            key={link.href}
            title={link.label}
            subtitle={heading}
            description={description}
            locale={currentLocale}
            isOpen={openSection === dropdownType}
            onToggle={() => toggleSection(dropdownType)}
          >
            {megaGroups
              ? megaGroups.map((group) => (
                  <div key={group.label} className="mt-1 first:mt-0">
                    <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-muted">
                      {getLabel(currentLocale, group.label)}
                    </p>
                    {group.items.map((item) => (
                      <MobileSubLink
                        key={item.href}
                        item={item}
                        pathname={pathname}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </div>
                ))
              : dropdownLinks.map((item) => (
                  <MobileSubLink
                    key={item.href}
                    item={item}
                    pathname={pathname}
                    onNavigate={onNavigate}
                  />
                ))}
          </MobileDropdownSection>
        );
      })}

      <MobileSupportCard locale={currentLocale} />

      <LanguageSwitcher pathname={pathname} variant="mobile" onNavigate={onNavigate} />

      <Link href={getLocalizedPathname("/contact/", currentLocale)} onClick={onNavigate}>
        <Button className="mt-1 w-full rounded-full bg-primary py-5 text-base font-semibold text-primary-foreground transition hover:bg-primary-emphasis">
          {getLabel(currentLocale, "Meet with Us")}
        </Button>
      </Link>
    </div>
  );
};

const MobileNavHeader = ({ locale }: { locale: Locale }) => (
  <div className="rounded-3xl border border-border bg-card p-4">
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
        <Sparkles className="h-5 w-5 text-primary" />
      </span>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-muted">
          {getLabel(locale, "Navigation")}
        </p>
        <p className="mt-1 text-lg font-semibold text-neutral-emphasis">
          {getLabel(locale, "Explore Anchor Sprint")}
        </p>
        <p className="mt-1 text-sm text-neutral-muted">
          {getLabel(
            locale,
            "Quickly find company insights, service details, and helpful resources.",
          )}
        </p>
      </div>
    </div>
  </div>
);

interface MobileNavLinkProps {
  link: NavLinkConfig;
  pathname: string;
  onNavigate: () => void;
}

const MobileNavLink = ({ link, pathname, onNavigate }: MobileNavLinkProps) => {
  const currentLocale = getLocaleFromPathname(pathname);
  const isActive = isNavLinkActive(link, pathname);

  return (
    <Link
      href={navHref(link.href, currentLocale)}
      onClick={onNavigate}
      className={cn(
        "flex items-center justify-between rounded-3xl border px-5 py-4 text-base font-semibold transition-colors",
        isActive
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border bg-card text-neutral-muted hover:border-neutral-border hover:text-neutral-emphasis",
      )}
    >
      <span>{getLabel(currentLocale, link.label)}</span>
      <ChevronRight className="h-5 w-5 text-neutral-muted/60" />
    </Link>
  );
};

interface MobileDropdownSectionProps {
  title: string;
  subtitle: string;
  description: string;
  locale: Locale;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const MobileDropdownSection = ({
  title,
  subtitle,
  description,
  locale,
  isOpen,
  onToggle,
  children,
}: MobileDropdownSectionProps) => (
  <div className="rounded-3xl border border-border bg-card p-4">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 text-left"
    >
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-muted">
          {getLabel(locale, subtitle)}
        </p>
        <p className="mt-1 text-lg font-semibold text-neutral-emphasis">
          {getLabel(locale, title)}
        </p>
        <p className="mt-1 text-sm text-neutral-muted">
          {getLabel(locale, description)}
        </p>
      </div>
      <ChevronDown className={cn("h-5 w-5 text-primary transition-transform", isOpen && "-scale-y-100")}
      />
    </button>
    {isOpen && <div className="mt-4 space-y-2 border-t border-dashed border-border pt-4">{children}</div>}
  </div>
);

interface MobileSubLinkProps {
  item: SubLinkConfig;
  pathname: string;
  onNavigate: () => void;
}

const MobileSubLink = ({ item, pathname, onNavigate }: MobileSubLinkProps) => {
  const currentLocale = getLocaleFromPathname(pathname);
  const isActive =
    getComparablePathname(pathname) === getComparablePathname(item.href);

  return (
    <Link
      href={navHref(item.href, currentLocale)}
      onClick={onNavigate}
      className={cn(
        "flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-medium transition-colors",
        isActive
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border text-neutral-muted hover:border-neutral-border hover:text-neutral-emphasis",
      )}
    >
      <span>{getLinkLabel(currentLocale, item)}</span>
      <ChevronRight className="h-4 w-4 text-neutral-muted/60" />
    </Link>
  );
};

const MobileSupportCard = ({ locale }: { locale: Locale }) => (
  <div className="rounded-3xl border border-warning/30 bg-warning/10 p-4">
    <p className="text-sm font-semibold text-warning">
      {getLabel(locale, "Need a tailored plan?")}
    </p>
    <p className="mt-1 text-sm text-neutral-muted">
      {getLabel(
        locale,
        "Chat with our team to craft the right engagement for your product vision.",
      )}
    </p>
  </div>
);

// English-only pages must not receive an ms/zh prefix.
function navHref(href: string, locale: Locale): string {
  return isEnglishOnlyRoute(href)
    ? getLocalizedPathname(href, "en")
    : getLocalizedPathname(href, locale);
}

interface LanguageSwitcherProps {
  pathname: string;
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}

const LanguageSwitcher = ({ pathname, variant, onNavigate }: LanguageSwitcherProps) => {
  // Hide the switcher when translated variants do not exist.
  if (isEnglishOnlyRoute(pathname)) return null;

  const currentLocale = getLocaleFromPathname(pathname);

  return (
    <div
      role="group"
      className={cn(
        "flex items-center rounded-full border border-border bg-card p-1",
        variant === "desktop" ? "gap-1" : "w-full justify-between",
      )}
      aria-label="Language selector"
    >
      {locales.map((locale) => {
        const isCurrent = locale === currentLocale;

        return (
          <Link
            key={locale}
            href={getLocalizedPathname(pathname, locale)}
            onClick={onNavigate}
            aria-label={`Switch language to ${getLanguageName(locale)}`}
            aria-current={isCurrent ? "page" : undefined}
            className={cn(
              "inline-flex items-center justify-center rounded-full text-xs font-semibold transition-colors",
              variant === "desktop" ? "h-8 min-w-9 px-3" : "h-10 flex-1 px-4",
              isCurrent
                ? "bg-primary text-primary-foreground"
                : "text-neutral-muted hover:bg-neutral-surface hover:text-neutral-emphasis",
            )}
          >
            {getLanguageLabel(locale)}
          </Link>
        );
      })}
    </div>
  );
};

export function Navbar() {
  const pathname = usePathname();
  const currentLocale = getLocaleFromPathname(pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownType | null>(null);

  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);
  const aiAgenticDropdownRef = useRef<HTMLDivElement>(null);
  const enterpriseDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openDropdown) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const refs: Record<DropdownType, HTMLDivElement | null> = {
        company: companyDropdownRef.current,
        services: serviceDropdownRef.current,
        "ai-agentic": aiAgenticDropdownRef.current,
        enterprise: enterpriseDropdownRef.current,
      };
      const activeRef = refs[openDropdown];

      if (activeRef && !activeRef.contains(target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const toggleDropdown = (type: DropdownType) => {
    setOpenDropdown((previous) => (previous === type ? null : type));
  };

  const closeDropdown = () => setOpenDropdown(null);

  const closeSheet = () => {
    setIsOpen(false);
  };

  // Hide the simple link for the page you're already on (declutters the bar and
  // nudges clicks toward other destinations). Dropdowns stay — you still need to
  // reach sibling pages from a service/product/AI sub-page.
  const visibleNavLinks = NAV_LINKS.filter(
    (link) => link.hasDropdown || !isNavLinkActive(link, pathname),
  );

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur">
      <CrawlableNav locale={currentLocale} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <NavbarLogo locale={currentLocale} />

          <DesktopNavigation
            navItems={visibleNavLinks}
            pathname={pathname}
            openDropdown={openDropdown}
            onToggleDropdown={toggleDropdown}
            onCloseDropdown={closeDropdown}
            dropdownRefs={{
              "ai-agentic": aiAgenticDropdownRef,
              enterprise: enterpriseDropdownRef,
              company: companyDropdownRef,
              services: serviceDropdownRef,
            }}
          />

          <div className="flex items-center xl:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menu" className="rounded-full border border-border bg-card hover:border-neutral-border">
                  <Menu className="h-6 w-6 text-neutral-emphasis" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full max-w-xs overflow-y-auto border-l border-border bg-background px-0 text-neutral-emphasis"
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>Navigation Menu</SheetTitle>
                  <SheetDescription>Navigate through the website sections and pages</SheetDescription>
                </SheetHeader>
                <div className="flex items-center gap-2 border-b border-border px-6 pb-4 pt-6 sm:gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-border bg-card">
                    <img src={logoPath} alt={`${t(currentLocale, "Anchor Sprint")} Logo`} className="h-8 w-8 rounded-xl object-contain" />
                  </span>
                  <div className="flex min-w-0 flex-col leading-tight">
                    <span className="text-sm font-semibold text-neutral-emphasis">{t(currentLocale, "Anchor Sprint")}</span>
                    <span className="whitespace-nowrap text-[0.6rem] font-medium uppercase tracking-[0.12em] text-neutral-muted sm:text-xs sm:tracking-[0.18em]">
                      {t(currentLocale, "AI Digital Agency")}
                    </span>
                  </div>
                </div>
                <div className="space-y-6 px-6 py-6">
                  <MobileNavigation navItems={visibleNavLinks} pathname={pathname} onNavigate={closeSheet} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
